import DOMPurify from 'dompurify';

/**
 * HTML encode special characters to prevent XSS attacks
 */
export function htmlEncode(input: string): string {
  if (typeof input !== 'string') return input;
  
  const div = document.createElement('div');
  div.textContent = input;
  return div.innerHTML;
}

/**
 * Sanitize HTML content using DOMPurify
 */
export function sanitizeHtml(dirty: string): string {
  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'u', 'br', 'p', 'span'],
    ALLOWED_ATTR: ['class'],
    KEEP_CONTENT: true
  });
}

/**
 * Sanitize text content by removing HTML tags and encoding entities
 */
export function sanitizeText(input: string): string {
  if (typeof input !== 'string') return input;
  
  // Remove all HTML tags
  const withoutTags = input.replace(/<[^>]*>/g, '');
  
  // Encode remaining entities
  return htmlEncode(withoutTags);
}

/**
 * Validate that a string doesn't contain potentially dangerous patterns
 */
export function validateInput(input: string): boolean {
  if (typeof input !== 'string') return true;
  
  const dangerousPatterns = [
    /<script[^>]*>.*?<\/script>/gi,
    /javascript:/gi,
    /on\w+\s*=/gi,
    /<iframe[^>]*>/gi,
    /vbscript:/gi,
    /data:text\/html/gi
  ];
  
  return !dangerousPatterns.some(pattern => pattern.test(input));
}

/**
 * Safe component props sanitizer for React components
 */
export function sanitizeProps<T extends Record<string, any>>(props: T): T {
  const sanitized = { ...props } as T;
  
  Object.keys(sanitized).forEach(key => {
    const value = sanitized[key];
    if (typeof value === 'string') {
      // Check if this looks like user input that should be sanitized
      if (key.toLowerCase().includes('content') || 
          key.toLowerCase().includes('text') || 
          key.toLowerCase().includes('message') ||
          key.toLowerCase().includes('name') ||
          key.toLowerCase().includes('title')) {
        (sanitized as any)[key] = sanitizeText(value);
      }
    }
  });
  
  return sanitized;
}

/**
 * Create a safe innerHTML setter using DOMPurify
 */
export function createSafeHTML(content: string) {
  return {
    __html: sanitizeHtml(content)
  };
}

/**
 * Input validation for Bitcoin addresses
 */
export function isValidBitcoinAddress(address: string): boolean {
  if (!address || typeof address !== 'string') return false;
  
  // Basic format validation
  if (address.length < 26 || address.length > 62) return false;
  
  // Check for valid characters
  const validChars = /^[a-km-zA-HJ-NP-Z1-9]+$/;
  if (!validChars.test(address)) return false;
  
  // Check for dangerous patterns
  return validateInput(address);
}

/**
 * Input validation for transaction hashes
 */
export function isValidTransactionHash(hash: string): boolean {
  if (!hash || typeof hash !== 'string') return false;
  
  // Bitcoin transaction hashes are 64 character hex strings
  if (hash.length !== 64) return false;
  
  // Check for valid hex characters
  const hexPattern = /^[a-fA-F0-9]+$/;
  if (!hexPattern.test(hash)) return false;
  
  // Check for dangerous patterns
  return validateInput(hash);
}

/**
 * Input validation for block hashes
 */
export function isValidBlockHash(hash: string): boolean {
  // Same validation as transaction hash for Bitcoin
  return isValidTransactionHash(hash);
}

/**
 * Input validation for block height
 */
export function isValidBlockHeight(height: string | number): boolean {
  const num = typeof height === 'string' ? parseInt(height, 10) : height;
  
  // Check if it's a valid positive integer within reasonable bounds
  return !isNaN(num) && 
         num >= 0 && 
         num <= 10000000 && // Reasonable upper limit
         num === Math.floor(num);
}

/**
 * Validate a token string for security
 */
export function validateToken(token: string | null): token is string {
  if (!token) return false;
  
  // Check length constraints
  if (token.length < 1 || token.length > 2048) return false;
  
  // Check format - only allow alphanumeric chars, dots, underscores, and hyphens
  const tokenRegex = /^[a-zA-Z0-9._-]+$/;
  if (!tokenRegex.test(token)) return false;
  
  // Check for XSS patterns
  const xssPatterns = [
    /<script/gi,
    /javascript:/gi,
    /on\w+\s*=/gi,
    /<iframe/gi,
    /vbscript:/gi,
    /data:text\/html/gi
  ];
  
  return !xssPatterns.some(pattern => pattern.test(token));
}

/**
 * Safely extract and validate a token from URL parameters
 */
export function extractSafeToken(searchParams: URLSearchParams, paramName: string = 'token'): string | null {
  const token = searchParams.get(paramName);
  
  if (!validateToken(token)) {
    console.warn(`Invalid or unsafe ${paramName} parameter detected`);
    return null;
  }
  
  return token;
}

/**
 * Validate URL for client-side redirects
 */
export function validateClientRedirectUrl(url: string, allowedDomains: string[] = []): boolean {
  try {
    const parsed = new URL(url, window.location.origin);
    
    // Only allow http and https protocols
    if (!['http:', 'https:'].includes(parsed.protocol)) {
      return false;
    }
    
    // If no allowed domains specified, only allow same origin
    if (allowedDomains.length === 0) {
      return parsed.origin === window.location.origin;
    }
    
    // Check if hostname matches any allowed domain
    return allowedDomains.some(domain => {
      return parsed.hostname === domain || 
             (parsed.hostname.endsWith(`.${domain}`) && parsed.hostname.length > domain.length + 1);
    });
  } catch {
    return false;
  }
}

/**
 * Safe client-side redirect with domain validation
 */
export function safeClientRedirect(url: string, allowedDomains: string[] = []): void {
  if (!validateClientRedirectUrl(url, allowedDomains)) {
    console.error('Blocked potentially unsafe redirect to:', url);
    // Fallback to home page for safety
    window.location.href = '/';
    return;
  }
  
  window.location.href = url;
}